import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  StatusPill                                                        */
/* ------------------------------------------------------------------ */

export type StatusPillVariant = "healthy" | "warning" | "error" | "neutral";

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: StatusPillVariant;
  /** Icon element rendered before the text. */
  icon?: React.ReactNode;
}

const css = /* css */ `
  .sf-status-pill {
    display: inline-flex;
    align-items: center;
    gap: var(--sf-space-1);
    font-family: var(--sf-font-sans);
    font-size: var(--sf-text-xs);
    font-weight: var(--sf-weight-medium);
    line-height: 1;
    padding: 4px 10px;
    border-radius: var(--sf-radius-full);
    white-space: nowrap;
    border: 1px solid transparent;
  }

  .sf-status-pill__icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  /* Healthy */
  .sf-status-pill--healthy {
    background: var(--sf-success-light);
    color: var(--sf-success);
    border-color: rgba(9, 146, 104, 0.15);
  }

  /* Warning */
  .sf-status-pill--warning {
    background: var(--sf-warning-light);
    color: var(--sf-warning);
    border-color: rgba(230, 119, 0, 0.15);
  }

  /* Error */
  .sf-status-pill--error {
    background: var(--sf-error-light);
    color: var(--sf-error);
    border-color: rgba(201, 42, 42, 0.15);
  }

  /* Neutral */
  .sf-status-pill--neutral {
    background: var(--sf-gray-100);
    color: var(--sf-text-secondary);
    border-color: var(--sf-gray-200);
  }
`;

/* Default icons per variant */
function DefaultIcon({ variant }: { variant: StatusPillVariant }) {
  const size = 12;
  switch (variant) {
    case "healthy":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 1a7 7 0 110 14A7 7 0 018 1zm3.03 4.97a.75.75 0 00-1.06 0L7 8.94 5.53 7.47a.75.75 0 10-1.06 1.06l2 2a.75.75 0 001.06 0l3.5-3.5a.75.75 0 000-1.06z" />
        </svg>
      );
    case "warning":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 3.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zM8 11a1 1 0 100 2 1 1 0 000-2z" />
        </svg>
      );
    case "error":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm2.03 4.97a.75.75 0 010 1.06L8.81 8.25l1.22 1.22a.75.75 0 11-1.06 1.06L7.75 9.31l-1.22 1.22a.75.75 0 11-1.06-1.06l1.22-1.22-1.22-1.22a.75.75 0 111.06-1.06l1.22 1.22 1.22-1.22a.75.75 0 011.06 0z" />
        </svg>
      );
    case "neutral":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-2 6.25a.75.75 0 000 1.5h4a.75.75 0 000-1.5H6z" />
        </svg>
      );
  }
}

const StatusPill = React.forwardRef<HTMLSpanElement, StatusPillProps>(
  ({ variant = "neutral", icon, children, className, ...rest }, ref) => {
    injectStyles("sf-status-pill", css);

    const classes = [
      "sf-status-pill",
      `sf-status-pill--${variant}`,
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <span ref={ref} className={classes} {...rest}>
        <span className="sf-status-pill__icon">
          {icon ?? <DefaultIcon variant={variant} />}
        </span>
        {children}
      </span>
    );
  },
);

StatusPill.displayName = "StatusPill";
export { StatusPill };
